import React from "react"
import { motion } from "framer-motion"
import { useHistory } from "react-router-dom"
import { HiOutlineArrowPathRoundedSquare, HiOutlineArrowRight, HiOutlineCheckCircle, HiOutlineClock, HiOutlineDocumentCheck } from "react-icons/hi2"
import { getCurrentEmployee, getEmployeeSopState, getSopAuditLog, resetSopProgress, trackSopOpen, trackSopReviewed, updateSopProgress } from "../Common/sopTracking"
import Dialog from "./Dialog"

const sopSections = [
    {
        id: "start-day",
        title: "Start the day",
        helper: "Begin here before handling any profile or plan update.",
        actionLabel: "Open Dashboard",
        actionPath: "/",
        items: [
            "Check dashboard cards for plans expiring today and this week.",
            "Review Pending, Paid Members, and Happy Customer queues before starting calls.",
            "Keep the SOP open while working so staff can follow the same order every day.",
        ],
    },
    {
        id: "profile-work",
        title: "Process user profiles",
        helper: "Use this for new registrations, corrections, and review work.",
        actionLabel: "Open User List",
        actionPath: "/user",
        items: [
            "Create or edit profiles only from the slider so work stays on one screen.",
            "Check mobile number, family details, native address, and images before saving.",
            "Move verification status only after profile details are complete and readable.",
        ],
    },
    {
        id: "plan-work",
        title: "Manage plans and payments",
        helper: "Use when assigning, correcting, or renewing a subscription.",
        actionLabel: "Open Plan Validity",
        actionPath: "/planValidity",
        items: [
            "Confirm the member plan name and validity before updating anything.",
            "Use inline plan update from the user list only after confirming the correct plan.",
            "Track today and this week expiry cards and finish follow-up before end of day.",
        ],
    },
    {
        id: "member-support",
        title: "Support members",
        helper: "Use row actions to avoid jumping between unrelated pages.",
        actionLabel: "Open Pending Users",
        actionPath: "/user/0",
        items: [
            "Use the row hamburger menu for view, edit, change password, images, and WhatsApp.",
            "Share only verified information with members and update notes before closing the task.",
            "If a user issue is not resolved, keep it in the correct queue instead of marking it complete.",
        ],
    },
    {
        id: "close-day",
        title: "Close the day",
        helper: "Finish these checks before staff logs out.",
        actionLabel: "Open Plans",
        actionPath: "/plan",
        items: [
            "Confirm high-priority pending profiles were reviewed or reassigned.",
            "Check expiring plans follow-up is complete for the day.",
            "Mark the SOP as reviewed today after completing the checklist.",
        ],
    },
]

const getTodayKey = () => new Date().toISOString().slice(0, 10)

const formatDateTime = (value) => {
    if (!value) return "Not tracked yet"

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return "Not tracked yet"

    return parsed.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    })
}

const StaffSopDialog = ({ open, onClose, onTrackingChange }) => {
    const history = useHistory()
    const employee = React.useMemo(() => getCurrentEmployee(), [])
    const [employeeState, setEmployeeState] = React.useState(() => getEmployeeSopState(employee.employeeId))
    const [auditLog, setAuditLog] = React.useState(() => getSopAuditLog())
    const [activeTab, setActiveTab] = React.useState("checklist")
    const wasOpenRef = React.useRef(false)

    React.useEffect(() => {
        if (open && !wasOpenRef.current) {
            const nextState = trackSopOpen(employee)
            const nextAudit = getSopAuditLog()
            setEmployeeState(nextState)
            setAuditLog(nextAudit)
            if (typeof onTrackingChange === "function") onTrackingChange(nextState)
        }

        wasOpenRef.current = open
    }, [employee, onTrackingChange, open])

    const progress = employeeState?.progress || {}
    const tracking = employeeState?.stats || {}
    const completedSteps = Object.values(progress).filter(Boolean).length
    const totalSteps = sopSections.reduce((sum, section) => sum + section.items.length, 0)
    const reviewedToday = tracking?.lastReviewedAt?.slice?.(0, 10) === getTodayKey()

    const toggleItem = (key) => {
        const nextProgress = {
            ...progress,
            [key]: !progress[key],
        }
        const nextState = updateSopProgress(employee, nextProgress)
        setEmployeeState(nextState)
    }

    const handleMarkReviewed = () => {
        const nextState = trackSopReviewed(employee)
        const nextAudit = getSopAuditLog()
        setEmployeeState(nextState)
        setAuditLog(nextAudit)
        if (typeof onTrackingChange === "function") onTrackingChange(nextState)
    }

    const handleResetChecklist = () => {
        const nextState = resetSopProgress(employee)
        setEmployeeState(nextState)
    }

    const openActionPath = (path) => {
        history.push(path)
        if (typeof onClose === "function") onClose()
    }

    return (
        <Dialog showDialog={open} onClose={onClose} title="Staff SOP" injectClass="max-w-5xl">
            <motion.div className="grid gap-5">
                {/* Tab Bar */}
                <div className="flex gap-1 rounded-2xl border border-borderColor bg-slate-50 p-1">
                    <button
                        type="button"
                        onClick={() => setActiveTab("checklist")}
                        className={`flex-1 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "checklist" ? "bg-white text-primeColor shadow-soft" : "text-tpColor hover:text-tColor"}`}
                    >
                        SOP Checklist
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab("access-log")}
                        className={`flex-1 rounded-xl px-4 py-2 text-sm font-bold transition ${activeTab === "access-log" ? "bg-white text-primeColor shadow-soft" : "text-tpColor hover:text-tColor"}`}
                    >
                        Access Log
                        {auditLog.length > 0 && (
                            <span className="ml-2 rounded-full bg-primeColor px-2 py-0.5 text-xs font-bold text-white">{auditLog.length}</span>
                        )}
                    </button>
                </div>

                {activeTab === "checklist" && (<>
                <div className="grid gap-4 lg:grid-cols-[1.5fr,1fr]">
                    <div className="rounded-3xl border border-primeColor/15 bg-primeSoft p-5">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primeColor">Daily operating guide</p>
                        <h4 className="mt-2 text-xl font-semibold text-tColor">Actionable workflow for staff</h4>
                        <p className="mt-3 text-sm leading-7 text-tColor">
                            Staff should complete these steps in order so profile review, plan handling, and member support stay consistent. This panel now records SOP access and review activity for the logged-in employee on this system.
                        </p>
                        <div className="mt-4 rounded-2xl border border-white/60 bg-white/70 px-4 py-3 text-sm text-tColor">
                            <p className="font-semibold">{employee.displayName}</p>
                            <p className="mt-1 text-tpColor">{employee.email} • {employee.role}</p>
                        </div>
                    </div>

                    <div className="grid gap-3 rounded-3xl border border-borderColor bg-white p-5 shadow-soft">
                        <div className="flex items-center gap-3">
                            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primeSoft text-primeColor">
                                <HiOutlineDocumentCheck className="text-xl" />
                            </span>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-tpColor">Usage tracking</p>
                                <p className="text-base font-semibold text-tColor">{reviewedToday ? "Reviewed today" : "Review pending today"}</p>
                            </div>
                        </div>
                        <div className="grid gap-2 text-sm text-tColor">
                            <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="flex items-center gap-2"><HiOutlineClock /> Last opened</span>
                                <span className="text-right font-semibold">{formatDateTime(tracking.lastOpenedAt)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                                <span className="flex items-center gap-2"><HiOutlineCheckCircle /> Last reviewed</span>
                                <span className="text-right font-semibold">{formatDateTime(tracking.lastReviewedAt)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                                <span>Checklist progress</span>
                                <span className="font-semibold">{completedSteps}/{totalSteps}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                                <span>SOP opens</span>
                                <span className="font-semibold">{tracking.openCount || 0}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3 pt-2">
                            <button
                                type="button"
                                className="inline-flex min-h-[44px] items-center justify-center rounded-2xl bg-primeColor px-4 py-2 text-sm font-bold text-white transition hover:bg-primeDark"
                                onClick={handleMarkReviewed}
                            >
                                Mark Reviewed Today
                            </button>
                            <button
                                type="button"
                                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border border-borderColor bg-white px-4 py-2 text-sm font-bold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                onClick={handleResetChecklist}
                            >
                                <HiOutlineArrowPathRoundedSquare />
                                Reset Checklist
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid gap-4">
                    {sopSections.map((section) => (
                        <div key={section.id} className="rounded-3xl border border-borderColor bg-white p-5 shadow-soft">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">{section.title}</p>
                                    <p className="mt-2 text-sm leading-7 text-tColor">{section.helper}</p>
                                </div>
                                <button
                                    type="button"
                                    className="inline-flex min-h-[42px] items-center gap-2 rounded-2xl border border-borderColor bg-slate-50 px-4 py-2 text-sm font-bold text-tColor transition hover:border-primeColor hover:text-primeColor"
                                    onClick={() => openActionPath(section.actionPath)}
                                >
                                    {section.actionLabel}
                                    <HiOutlineArrowRight />
                                </button>
                            </div>

                            <div className="mt-4 grid gap-3">
                                {section.items.map((item, index) => {
                                    const itemKey = `${section.id}-${index}`
                                    const checked = Boolean(progress[itemKey])

                                    return (
                                        <label
                                            key={itemKey}
                                            className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
                                                checked
                                                    ? "border-primeColor/30 bg-primeSoft/70"
                                                    : "border-borderColor bg-slate-50 hover:border-primeColor/30 hover:bg-white"
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="mt-1 h-4 w-4 accent-[#E74C3C]"
                                                checked={checked}
                                                onChange={() => toggleItem(itemKey)}
                                            />
                                            <span className="text-sm leading-7 text-tColor">{item}</span>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                </>)}

                {activeTab === "access-log" && (
                <div className="rounded-3xl border border-borderColor bg-white p-5 shadow-soft">
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Access log</p>
                            <p className="mt-1 text-sm text-tColor">Recent SOP activity recorded for staff on this system.</p>
                        </div>
                        <span className="rounded-full border border-borderColor bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-tpColor">
                            {auditLog.length} recent events
                        </span>
                    </div>

                    <div className="mt-4 overflow-hidden rounded-2xl border border-borderColor">
                        <div className="grid grid-cols-[1.3fr_1.2fr_0.8fr_1fr] gap-3 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-tpColor">
                            <span>Employee</span>
                            <span>Contact</span>
                            <span>Action</span>
                            <span>Time</span>
                        </div>
                        <div className="divide-y divide-borderColor">
                            {auditLog.slice(0, 20).map((entry) => (
                                <div key={entry.id} className="grid grid-cols-[1.3fr_1.2fr_0.8fr_1fr] gap-3 px-4 py-3 text-sm text-tColor">
                                    <span className="font-semibold">{entry.employeeName}</span>
                                    <span className="text-tpColor">{entry.employeeEmail}</span>
                                    <span className={`font-semibold ${entry.type === "reviewed" ? "text-emerald-600" : "text-primeColor"}`}>
                                        {entry.type === "reviewed" ? "Reviewed" : "Opened"}
                                    </span>
                                    <span className="text-tpColor">{formatDateTime(entry.at)}</span>
                                </div>
                            ))}
                            {auditLog.length === 0 && (
                                <div className="px-4 py-6 text-sm text-tpColor">No SOP activity has been tracked yet.</div>
                            )}
                        </div>
                    </div>
                </div>
                )}
            </motion.div>
        </Dialog>
    )
}

export default StaffSopDialog
